import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Send, Bot, User, Sparkles } from 'lucide-react';
import { mockChatResponses } from '../data/mock';

const AIChat = () => {
  /** Окно чата всегда видимо на страницах, где компонент подключён */
  const isOpen = true;
  const [messages, setMessages] = useState([
    {
      id: 1,
      role: 'assistant',
      content: 'Здравствуйте! Я Антоша AI-консультант. Расскажите, какую бизнес-задачу вы хотите решить?',
    },
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const getMockResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    if (lowerMessage.includes('crm') || lowerMessage.includes('сдел') || lowerMessage.includes('продаж')) {
      return mockChatResponses.crm[Math.floor(Math.random() * mockChatResponses.crm.length)];
    }
    if (lowerMessage.includes('телефон') || lowerMessage.includes('звон') || lowerMessage.includes('атс')) {
      return mockChatResponses.telephony[Math.floor(Math.random() * mockChatResponses.telephony.length)];
    }
    if (lowerMessage.includes('ai') || lowerMessage.includes('ии') || lowerMessage.includes('нейро') || lowerMessage.includes('искусствен')) {
      return mockChatResponses.ai[Math.floor(Math.random() * mockChatResponses.ai.length)];
    }
    if (lowerMessage.includes('поддержк') || lowerMessage.includes('нагрузк') || lowerMessage.includes('оператор')) {
      return mockChatResponses.support[Math.floor(Math.random() * mockChatResponses.support.length)];
    }
    if (lowerMessage.includes('привет') || lowerMessage.includes('здравств')) {
      return mockChatResponses.greetings[Math.floor(Math.random() * mockChatResponses.greetings.length)];
    }
    
    return mockChatResponses.default[Math.floor(Math.random() * mockChatResponses.default.length)];
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      role: 'user',
      content: input.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate AI response delay
    setTimeout(() => {
      const response = getMockResponse(userMessage.content);
      setMessages((prev) => [
        ...prev,
        {
          id: prev.length + 1,
          role: 'assistant',
          content: response,
        },
      ]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Chat Toggle Button - скрыта, открывается только через окно чата */}

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed bottom-36 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] h-[500px] flex flex-col bg-card border-border/50 shadow-2xl animate-scale-in">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-border/50">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Антоша AI-консультант</h3>
              <p className="text-xs text-muted-foreground">Подберём решение для вас</p>
            </div>
          </div>

          {/* Messages */}
          <ScrollArea className="flex-1 p-4">
            <div className="space-y-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-3 ${
                    message.role === 'user' ? 'flex-row-reverse' : ''
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      message.role === 'user' ? 'bg-accent/10' : 'bg-muted'
                    }`}
                  >
                    {message.role === 'user' ? (
                      <User className="w-4 h-4 text-accent" />
                    ) : (
                      <Bot className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <div
                    className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                      message.role === 'user'
                        ? 'bg-accent text-accent-foreground rounded-tr-md'
                        : 'bg-muted rounded-tl-md'
                    }`}
                  >
                    {message.content}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
                    <Bot className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="bg-muted px-4 py-3 rounded-2xl rounded-tl-md">
                    <div className="typing-indicator flex gap-1">
                      <span className="w-2 h-2 bg-muted-foreground/50 rounded-full" />
                      <span className="w-2 h-2 bg-muted-foreground/50 rounded-full" />
                      <span className="w-2 h-2 bg-muted-foreground/50 rounded-full" />
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Input */}
          <div className="p-4 border-t border-border/50">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Опишите вашу задачу..."
                className="flex-1 bg-muted/50 border-border/50 focus:border-accent"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || isTyping}
                className="bg-accent hover:bg-accent/90 text-accent-foreground"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center">
              AI-ответы • Демо-режим
            </p>
          </div>
        </Card>
      )}
    </>
  );
};

export default AIChat;
